import { useCallback, useEffect, useRef, useState } from "react";
import { advanceSearch } from "../../actions";
import { useViewConfig } from "./ViewConfigContext";
import { displayValue, fieldsFor, setting, typeInfo } from "../../utils/viewConfig";

// One line under a result is enough in a menu.
const DETAIL_LENGTH = 80;

// A result as the top-bar menus show it: name, type label and colour, and the
// first configured quick-search field as a line of detail.
export function toOption(config, row) {
  const info = typeInfo(config, row.Type);
  const [first] = fieldsFor(config, "quickSearch", row);
  const detail = first ? displayValue(row[first]) : "";
  return {
    guid: row.GUID,
    name: row.name || info.label,
    type: row.Type,
    typeLabel: info.label,
    colour: info.colour,
    description: row.Description || "",
    detail: detail.length > DETAIL_LENGTH ? `${detail.slice(0, DETAIL_LENGTH)}…` : detail,
    diagramOf: row._diagramOf || null,
  };
}

// Quick search in the top bar: asks the server (which applies the plan and
// the quick-search visibility), debounced, with the configured result count.
export default function useQuickSearch({ delay = 300 } = {}) {
  const config = useViewConfig();
  const configRef = useRef(config);
  configRef.current = config;
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);
  const latest = useRef(0);

  useEffect(() => () => clearTimeout(timer.current), []);

  const search = useCallback(
    (term) => {
      clearTimeout(timer.current);
      const value = (term || "").trim();
      if (value.length < 2) {
        // Invalidate any request already in flight: without this, a slow
        // answer for a term the reader has since cleared could still land
        // and fill a box they emptied on purpose.
        latest.current += 1;
        setResults([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      timer.current = setTimeout(async () => {
        const id = ++latest.current;
        const current = configRef.current;
        const res = await advanceSearch("name", value, 0, setting(current, "quickSearch.limit"), "name", "ASC", "", "quick");
        if (id !== latest.current) return;
        setSearching(false);
        setResults(res?.success ? (res.content || []).map((row) => toOption(current, row)) : []);
      }, delay);
    },
    [delay]
  );

  return { results, searching, search };
}
